
import { Component, TemplateRef, ViewChild, computed, inject, input, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom, map, of } from 'rxjs';
import { AuthenticationService } from '../../authentication/authentication.service';
import { BillingChargeService, CreditAction } from '../../core/billing-charge.service';
import { LocalStorageService } from '../../core/localStorageService';
import { MceApiService, ProjectCommentsDto, ProjectDto, ProjectUpdateRequestDto } from '../../core/mceapi.client.generated';
import { MceDataTableComponent } from '../../shared/components/mce-data-table/mce-data-table.component';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { isSome, Option, none } from '../../shared/functions/functional';
import { ProjectEditComponent } from '../project-edit/project-edit.component';
import { ProjectEditService } from '../project-edit/project-edit.service';
import { ProjectUsersComponent } from '../project-users/project-users.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver } from '@angular/cdk/layout';
import { hasRole, Role } from '../../shared/enums/role';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';

interface DataTable {
  headers: { [key: string]: string; };
  displayedColumns: string[];
  templates: { [key: string]: TemplateRef<any>; };
  showSelect: boolean;
  data: ProjectDto[];
}

enum DisplayMode {
  DEFAULT = 'DEFAULT',
  ADD = 'ADD',
  EDIT = 'EDIT',
  USERS = 'USERS'
}


@Component({
  selector: 'mce-project-list',
  imports: [MceDataTableComponent, ProjectEditComponent, ProjectUsersComponent, MatToolbarModule,
    MatIconModule, MatProgressBarModule, MatProgressSpinnerModule, MatMenuModule, MatButtonModule,
    ClipboardModule, MatTooltipModule, InfoMessageComponent, RouterLink],
  templateUrl: './project-list.component.html',
})
export class ProjectListComponent {

  @ViewChild('logo', { static: true }) logo!: TemplateRef<any>;
  @ViewChild('rowActions', { static: true }) rowActions!: TemplateRef<any>;

  private readonly projectEditor = viewChild(ProjectEditComponent);
  readonly hasUnsavedChanges = computed(() => this.projectEditor()?.form().dirty() ?? false);

  private breakpointObserver = inject(BreakpointObserver);

  displayMode = signal<DisplayMode>(DisplayMode.DEFAULT);
  editProject = signal<ProjectDto>({});
  editComments = signal<Option<ProjectCommentsDto>>(none);
  processing = signal(false);

  private authenticationService = inject(AuthenticationService);
  private billingChargeService = inject(BillingChargeService);

  protected readonly isSharedBillingMember = computed(() =>
    hasRole(this.authenticationService.currentUserRoles(), Role.SharedBillingMember));
  private projectEditService = inject(ProjectEditService);
  private mceApiService = inject(MceApiService);
  private dialogService = inject(DialogService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);

  readonly isSmallDevice = toSignal(
    this.breakpointObserver.observe('(max-width: 640px)').pipe(
      map(result => result.matches)
    ),
    { initialValue: false }
  );


  private projectsResource = rxResource({
    params: () => ({ user: this.authenticationService.currentUser() }),
    stream: ({ params }) => {
        return isSome(params.user) && params.user.value.uid
          ? this.mceApiService.v1ProjectsUser(params.user.value.uid)
          : of([]);
    }
  });
  projects = computed<ProjectDto[]>(() => this.projectsResource.value() ?? []);

  protected readonly dataTable = computed<DataTable>(() => {

    const displayedColumns = this.isSmallDevice()
      ? ['projectNumber', 'projectName', 'actions']
      : ['projectNumber', 'projectName', 'logoURL', 'actions'];


    return {
      headers: { 'projectNumber': 'Project Number', 'projectName': 'Project Name', 'logoURL': ' ', 'actions': ' ' },
      displayedColumns: displayedColumns,
      templates: {'logoURL': this.logo, 'actions': this.rowActions },
      showSelect: false,
      data: this.projects(),
    };
  });


  protected readonly viewModel = computed(() => {
    const roles = this.authenticationService.currentUserRoles();
    const freeTier = hasRole(roles, Role.FreeTier);
    const proTier = hasRole(roles, Role.ProTier);
    const freeCreditsUser = hasRole(roles, Role.FreeCreditsUser);
    const projectCount = this.projects().length;
    const projectsLoading = this.projectsResource.isLoading();
    const freePlanRestrictionsApply = freeTier && !proTier && !freeCreditsUser && !this.isSharedBillingMember();
    const firstProjectIsFree = freePlanRestrictionsApply && !projectsLoading && projectCount === 0;
    const freePlanLimitReached = freePlanRestrictionsApply && projectCount === 1;
    const freePlanLimitExceeded = freePlanRestrictionsApply && projectCount > 1;
    const canCreateProject = (freeTier && projectCount === 0) || proTier || freeCreditsUser || this.isSharedBillingMember();
    const projectCreationDisabled = !canCreateProject;
    const disableGettingStartedMsg = projectCount > 0;

    return {

      dataTable: this.dataTable(),

      editProject: this.editProject(),
      editComments: this.editComments(),

      showLoading: projectsLoading,
      showSpinner: this.processing(),

      showDatatable:
        !projectsLoading && (this.displayMode() === DisplayMode.DEFAULT),

      showEditForm:
        (this.displayMode() === DisplayMode.ADD) || (this.displayMode() === DisplayMode.EDIT),

      projectCreationDisabled,
      firstProjectIsFree,
      freePlanLimitReached,
      freePlanLimitExceeded,
      disableGettingStartedMsg,
      showUsersForm: this.displayMode() === DisplayMode.USERS,

      showDebugPanel: window.location.href.toLowerCase().indexOf('localhost') > 0

    };
  });

  protected get vm() { return this.viewModel(); }

  async addProject() {

    if (this.vm.projectCreationDisabled) return;

    try {
      this.processing.set(true);

      const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
        action: CreditAction.ProjectAdd,
      }));

      const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
      if (!canProceed) return;

      let createProject = await this.projectEditService.createProject();
      if (!createProject.project) return;

      this.editProject.set(createProject.project);
      this.editComments.set(none);
      this.displayMode.set(DisplayMode.ADD);
    } finally {
      this.processing.set(false);
    }

   }

  async onEditProject(project: ProjectDto) {
    if (!project.uid || this.vm.freePlanLimitExceeded) return;

    try {
      this.processing.set(true);

      const comments = await this.projectEditService.getProjectComments(project.uid);

      this.editProject.set(project);
      this.editComments.set(comments);
      this.displayMode.set(DisplayMode.EDIT);
    } finally {
      this.processing.set(false);
    }
  }

  onGoCables(project: ProjectDto) {
    if (!project.uid || this.vm.freePlanLimitExceeded) return;

    this.localStorageService.recentProjectsAddProject(project.uid);
    this.router.navigate(['/cables', project.uid]);
  }

  onUsers(project: ProjectDto) {
    if (this.vm.freePlanLimitExceeded) return;

    this.editProject.set(project);
    this.displayMode.set(DisplayMode.USERS);
  }

  async onCopyProject(project: ProjectDto) {
    if (!project.uid || this.vm.projectCreationDisabled) return;

    const mergeProject = (projects: ProjectDto[], updatedProject: ProjectDto) =>
      projects.find(p => p.uid === updatedProject.uid)
        ? projects.map(p => p.uid === updatedProject.uid ? updatedProject : p)
        : [updatedProject, ...projects];

    try {
      this.processing.set(true);

      const cableCountResult = await firstValueFrom(this.mceApiService.v1CablesProjectCount(project.uid));
      const cableCount = cableCountResult.value ?? 0;

      const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
        action: CreditAction.ProjectCopy,
        cableCount,
      }));

      const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
      if (!canProceed) return;

      const totalCredits = chargeQuote.quote?.cost ?? 0;
      const copySummary = this.isSharedBillingMember()
        ? `You are copying this project and its <b>${cableCount}</b> cables.`
        : `Copying this project and its <b>${cableCount}</b> cables will charge <b>${totalCredits}</b> credits.`;
      const confirmed = await this.dialogService.confirmAsync(
        'Confirm Copy Project',
        `${project.projectNumber}<br />${project.projectName}<br /><br />${copySummary}<br /><br />Are you sure you want to continue?`,
        '320px'
      );
      if (!confirmed) return;

      let copiedProject = await this.projectEditService.copyProject(project);

      await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));

      this.projectsResource.set(mergeProject(this.projects(), copiedProject));
    } finally {
      this.processing.set(false);
    }
  }

  async onDeleteProject(project: ProjectDto) {

    let confirmed = await this.dialogService.confirmAsync(
      'Confirm Delete Project',
      `${project.projectNumber}<br />${project.projectName}<br /><br />
      Are you sure you want to delete this project (including all cables)?`,
      "260px"
    );
    if (!confirmed) return;

    try {
      this.processing.set(true);
      await this.projectEditService.deleteProject(project);
      this.projectsResource.set(this.projects().filter(x => x.uid !== project.uid));
    } finally {
      this.processing.set(false);
    }
  }

  async onEditSave(request: ProjectUpdateRequestDto) {

    let updateProjects = (projects:ProjectDto[], updatedProject:ProjectDto) =>
      projects.find(p => p.uid === updatedProject.uid)
        ? projects.map(p => p.uid === updatedProject.uid ? updatedProject : p) // update existing project
        : [updatedProject, ...projects];  // add

    try {
      this.processing.set(true);

      const displayMode = this.displayMode();
      const chargeQuote = displayMode === DisplayMode.ADD
        ? await firstValueFrom(this.billingChargeService.getQuote({
          action: CreditAction.ProjectAdd,
        }))
        : undefined;

      if (chargeQuote) {
        const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
        if (!canProceed) return;
      }

      let updatedProject =
        displayMode === DisplayMode.EDIT
          ? await this.projectEditService.updateProject(request)
          : await this.projectEditService.saveNewProject(request);

      if (chargeQuote) {
        await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));
      }

      this.projectsResource.set(updateProjects(this.projects(), updatedProject));
      this.displayMode.set(DisplayMode.DEFAULT);
    } finally {
      this.processing.set(false);
    }

  }

  onCancel() { this.displayMode.set(DisplayMode.DEFAULT); }

}
